/**
 * Clear client-side journey session state (UI/session only).
 * Does NOT affect data already saved on the backend (lead, quote, etc.).
 *
 * @param {Object} [options]
 * @param {boolean} [options.clearLocalStorage=false] - Also clear application number and case id from localStorage (full reset to start path).
 * @param {boolean} [options.purgeReduxPersist=false] - Remove redux-persist session key (use on explicit restart only).
 */
import {
  clearStoredApplicationNumber,
  clearStoredCaseId,
  clearStoredSessionRef,
} from './termJourneyStorage';
import { clearJourneySnapshot } from './journeySnapshot';

/** Key used to persist the in-app path where the user started the journey (for inactivity restart). */
export const JOURNEY_START_PATH_KEY = 'journeyStartPath';

/** Key for the full browser URL of the page from where the user started the journey. */
export const JOURNEY_INITIATED_PAGE_URL_KEY = 'journeyInitiatedPageUrl';

const SESSION_KEYS = [
  'leadCompleted',
  'journeyState',
  'inlic_lead_form_data',
  // Do not remove persist:root here — it wipes redux-persist session storage and breaks payment-return resume.
  JOURNEY_START_PATH_KEY,
  JOURNEY_INITIATED_PAGE_URL_KEY,
];

export function clearJourneySessionState(options = {}) {
  const { clearLocalStorage = false, purgeReduxPersist = false } = options;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
      if (purgeReduxPersist) {
        window.sessionStorage.removeItem('persist:root');
      }
    }
    if (clearLocalStorage) {
      clearStoredApplicationNumber();
      clearStoredCaseId();
      clearStoredSessionRef();
      // Full reset: also clear redux-persist root persisted in localStorage.
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem('persist:root');
        }
      } catch {
        /* ignore */
      }
    }
    if (purgeReduxPersist && typeof window !== 'undefined' && window.localStorage) {
      clearStoredSessionRef();
      window.localStorage.removeItem('persist:root');
    }
    // Always clear explicit journey snapshot for a hard reset/restart.
    clearJourneySnapshot();
  } catch {
    // Ignore storage errors (e.g. private mode)
  }
}

/**
 * Path from where the journey was initiated, based on current page URL.
 * Used as fallback when no stored journeyStartPath exists.
 */


export function getJourneyStartPath() {
  return '/home';
}
/**
 * Get the path to send the user to after inactivity restart: prefer stored
 * journeyStartPath (where they actually started this session), else fallback to URL-based path.
 */
export function getRestartPath() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = window.sessionStorage.getItem(JOURNEY_START_PATH_KEY);
      if (stored === '/' || stored === '/home') return stored;
    }
  } catch {
    /* ignore */
  }
  return getJourneyStartPath();
}

/**
 * Full browser URL of the page from where the user started the journey.
 * Used to redirect there on inactivity restart so the address bar shows the initiated page.
 */
export function getRestartPageUrl() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const url = window.sessionStorage.getItem(JOURNEY_INITIATED_PAGE_URL_KEY);
      if (url && typeof url === 'string' && url.startsWith('http')) return url;
    }
  } catch {
    /* ignore */
  }
  return null;
}
